import { ScoringBand, type StudyRunCaseDashboardStageHeatmapRowDto } from '@@types/api.types';
import { Tile } from '@components/shared/tile.component';
import { Badge, type BadgeProps } from '@components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select';
import { Skeleton } from '@components/ui/skeleton';
import { cn } from '@lib/utils';
import type { FC } from 'react';
import {
  type BriefSyncCell,
  type BriefThreadPhaseRollupRow,
  briefSyncWindows,
  getSyncMatrixRows,
} from './meia-p5-brief.util';

type BriefSyncMatrixTileProps = {
  isLoading: boolean;
  onSelectThread?: (threadId: string) => void;
  rollupRows?: BriefThreadPhaseRollupRow[];
  selectedThreadId?: string;
  stages: StudyRunCaseDashboardStageHeatmapRowDto[];
  threadOptions?: { label: string; value: string }[];
};

export const BriefSyncMatrixTile: FC<BriefSyncMatrixTileProps> = (props) => {
  const { isLoading, onSelectThread, rollupRows = [], selectedThreadId, stages, threadOptions = [] } = props;
  const rows = getSyncMatrixRows(stages);
  const isRollup = Boolean(selectedThreadId === 'all' && rollupRows.length > 0);

  if (isLoading) {
    return (
      <Tile header={{ title: 'Synchronization Matrix' }}>
        <Skeleton className="h-56 w-full" />
      </Tile>
    );
  }

  return (
    <Tile
      className="overflow-hidden"
      contentClassName="gap-3"
      header={{
        actions:
          threadOptions.length > 0 && selectedThreadId && onSelectThread ? (
            <div className="flex min-w-0 items-center gap-2">
              <Select
                onValueChange={onSelectThread}
                value={selectedThreadId}
              >
                <SelectTrigger className="h-8 w-56 min-w-0 bg-white text-xs text-gray-800 sm:w-72 lg:w-80">
                  <SelectValue placeholder="Select thread" />
                </SelectTrigger>
                <SelectContent className="max-w-80">
                  {threadOptions.map((option) => (
                    <SelectItem
                      key={option.value}
                      textValue={option.label}
                      value={option.value}
                    >
                      <span
                        className="block truncate"
                        title={option.label}
                      >
                        {option.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : undefined,
        title: 'Synchronization Matrix',
      }}
    >
      <p className="text-xs text-gray-500">
        Phase timing map across the decision window. Use it to spot where normal work, peak load, and cross-stage
        overlap concentrate before the review point.
      </p>
      <div className="flex flex-wrap items-center gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
        <SyncLegendItem
          label="Nominal"
          tone="green"
        />
        <SyncLegendItem
          label="Peak"
          tone="yellow"
        />
        <SyncLegendItem
          label="Overlap"
          tone="blue"
        />
        <SyncLegendItem label="Inactive" />
      </div>
      {isRollup ? (
        <BriefThreadPhaseRollupTable rows={rollupRows} />
      ) : rows.length === 0 ? (
        <p className="text-xs text-gray-500">No scored stages available for the selected thread.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-gray-200">
          <table className="min-w-full text-xs">
            <thead className="border-b border-gray-200 bg-gray-50 text-gray-500">
              <tr>
                <th className="sticky left-0 bg-gray-50 px-3 py-2 text-left font-medium">Stage</th>
                {briefSyncWindows.map((window) => (
                  <th
                    className="px-2 py-2 text-center font-mono text-[10px] font-medium"
                    key={window}
                  >
                    {window}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  className="border-b border-gray-100 last:border-b-0"
                  key={row.stageId}
                >
                  <td className="sticky left-0 bg-white px-3 py-2 font-medium text-gray-800">{row.stageName}</td>
                  {row.cells.map((cell) => (
                    <td
                      className="px-1 py-2"
                      key={`${row.stageId}-${cell.window}`}
                    >
                      <div className={cn('h-4 rounded-sm', syncCellClassName(cell))} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Tile>
  );
};

const BriefThreadPhaseRollupTable: FC<{ rows: BriefThreadPhaseRollupRow[] }> = (props) => {
  const { rows } = props;
  const phaseNames = Array.from(new Set(rows.flatMap((row) => row.stages.map((stage) => stage.stageName))));

  if (phaseNames.length === 0) {
    return <p className="text-xs text-gray-500">No scored thread phases available for the selected run case.</p>;
  }

  return (
    <div className="overflow-x-auto rounded-md border border-gray-200">
      <table className="min-w-full text-xs">
        <thead className="border-b border-gray-200 bg-gray-50 text-gray-500">
          <tr>
            <th className="sticky left-0 bg-gray-50 px-3 py-2 text-left font-medium">Thread</th>
            {phaseNames.map((phaseName) => (
              <th
                className="px-3 py-2 text-left font-medium"
                key={phaseName}
              >
                {phaseName}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              className="border-b border-gray-100 last:border-b-0"
              key={row.threadId}
            >
              <td className="sticky left-0 min-w-[220px] max-w-[260px] bg-white px-3 py-2">
                <div
                  className="truncate font-medium text-gray-800"
                  title={row.threadName}
                >
                  {row.threadName}
                </div>
                <div
                  className="truncate text-[10px] text-gray-500"
                  title={row.missionName}
                >
                  {row.missionName}
                </div>
              </td>
              {phaseNames.map((phaseName) => {
                const stage = row.stages.find((candidate) => candidate.stageName === phaseName);
                return (
                  <td
                    className="min-w-[120px] px-3 py-2"
                    key={`${row.threadId}-${phaseName}`}
                  >
                    {stage ? (
                      <Badge
                        size="xs"
                        variant={syncBandBadgeVariant(stage.verdictBand)}
                      >
                        {stage.verdictBand ?? 'Pending'}
                      </Badge>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

type SyncLegendItemProps = {
  label: string;
  tone?: 'blue' | 'default' | 'green' | 'yellow';
};

const SyncLegendItem: FC<SyncLegendItemProps> = (props) => {
  const { label, tone = 'default' } = props;

  return (
    <span className="inline-flex items-center gap-1">
      <span
        className={cn(
          'size-2.5 rounded-full',
          tone === 'blue' && 'bg-primary',
          tone === 'default' && 'bg-gray-300',
          tone === 'green' && 'bg-emerald-500',
          tone === 'yellow' && 'bg-amber-500',
        )}
      />
      {label}
    </span>
  );
};

const syncCellClassName = (cell: BriefSyncCell): string => {
  switch (cell.load) {
    case 'inactive':
      return 'bg-gray-100';
    case 'normal':
      return 'bg-emerald-500';
    case 'overlap':
      return 'bg-primary';
    case 'peak':
      return 'bg-amber-500';
  }
};

const syncBandBadgeVariant = (band?: ScoringBand | null): BadgeProps['variant'] => {
  switch (band) {
    case ScoringBand.GREEN:
      return 'success';
    case ScoringBand.RED:
      return 'destructive';
    case ScoringBand.YELLOW:
      return 'warning';
    case ScoringBand.PENDING:
    case null:
    case undefined:
      return 'pending';
  }
};
